// Serviço para comunicação com endpoints de analytics
import { API_ENDPOINTS } from "../shared/constants";
import {
  AlertsResponse,
  AnalyticsData,
  AnalyticsStatusResponse,
  CacheStatsResponse,
  CampaignInsightsResponse,
  DashboardData,
  HashtagRecommendationsResponse,
  IntelligentSuggestionsResponse,
  MLPerformanceResponse,
  MonitoringMetricsResponse,
  OptimalTimingResponse,
  PostData,
  PredictionData,
  RealTimeDashboardResponse,
  SentimentAnalysis,
  TrendAnalysisResponse,
} from "../types/analytics";
import { analyticsCache } from "../utils/smartCache";
import apiService from "../shared/apiService";
import { getActiveContextForApi } from "../shared/activeContextHolder";

class AnalyticsService {
  // ===== MÉTODOS DE ANALYTICS =====

  /**
   * Obter dashboard de performance geral
   */
  async getPerformanceDashboard(): Promise<DashboardData> {
    const ctx = getActiveContextForApi();
    const ctxKey = ctx
      ? `${ctx.type}:${ctx.organizationId ?? ""}:${ctx.clientCompanyId ?? ""}`
      : "anon";
    const cacheKey = `analytics:dashboard:${ctxKey}`;

    return analyticsCache.getPerformanceDashboard(async () => {
      return await apiService.get<DashboardData>(
        API_ENDPOINTS.ANALYTICS.PERFORMANCE_DASHBOARD,
      );
    }, cacheKey);
  }

  /**
   * Treinar modelos de machine learning
   */
  async trainModels(): Promise<{
    success: boolean;
    message: string;
    data_summary?: any;
  }> {
    // Backend expõe treinamento em /api/ml/train (ml_bp)
    return await apiService.post(API_ENDPOINTS.ML.TRAIN);
  }

  /**
   * Predizer engajamento de um post
   */
  async predictEngagement(postData: PostData): Promise<PredictionData> {
    return analyticsCache.getPredictions(postData, async () => {
      // Endpoint no backend: /api/analytics/engagement-prediction (ml_bp)
      return await apiService.post<PredictionData>(
        API_ENDPOINTS.ANALYTICS.ENGAGEMENT_PREDICTION,
        postData,
      );
    });
  }

  /**
   * Obter timing otimizado para publicação
   */
  async getOptimalTiming(contentData: {
    content: string;
    platform: string;
    target_audience?: string;
  }): Promise<OptimalTimingResponse> {
    return await apiService.post<OptimalTimingResponse>(
      API_ENDPOINTS.ANALYTICS.OPTIMAL_TIMING,
      contentData,
    );
  }

  /**
   * Obter insights de campanha
   */
  async getCampaignInsights(): Promise<CampaignInsightsResponse> {
    return await apiService.get<CampaignInsightsResponse>(
      API_ENDPOINTS.ANALYTICS.CAMPAIGN_INSIGHTS,
    );
  }

  /**
   * Obter sugestões inteligentes
   */
  async getIntelligentSuggestions(): Promise<IntelligentSuggestionsResponse> {
    const ctx = getActiveContextForApi();
    const ctxKey = ctx
      ? `${ctx.type}:${ctx.organizationId ?? ""}:${ctx.clientCompanyId ?? ""}`
      : "anon";
    const cacheKey = `analytics:insights:${ctxKey}`;

    return analyticsCache.getInsights(async () => {
      return await apiService.get<IntelligentSuggestionsResponse>(
        API_ENDPOINTS.ANALYTICS.INTELLIGENT_SUGGESTIONS,
      );
    }, cacheKey);
  }

  /**
   * Análise de sentimento
   */
  async getSentimentAnalysis(
    comments: string[],
    postId?: number,
  ): Promise<{
    success: boolean;
    analysis: SentimentAnalysis;
  }> {
    const ctx = getActiveContextForApi();
    const ctxKey = ctx
      ? `${ctx.type}:${ctx.organizationId ?? ""}:${ctx.clientCompanyId ?? ""}`
      : "anon";
    const cacheKey = `analytics:sentiment:${ctxKey}:${postId ?? "agg"}`;
    return analyticsCache.getSentiment(async () => {
      return await apiService.post<{
        success: boolean;
        analysis: SentimentAnalysis;
      }>(API_ENDPOINTS.ANALYTICS.SENTIMENT_ANALYZE, {
        comments,
        post_id: postId,
      });
    }, cacheKey);
  }

  /**
   * Recomendações de hashtags
   */
  async getHashtagRecommendations(
    content: string,
    platform: string,
  ): Promise<HashtagRecommendationsResponse> {
    return await apiService.post<HashtagRecommendationsResponse>(
      API_ENDPOINTS.ANALYTICS.HASHTAG_RECOMMENDATIONS,
      {
        content,
        platform,
      },
    );
  }

  /**
   * Análise de tendências
   */
  async getTrendAnalysis(): Promise<TrendAnalysisResponse> {
    const ctx = getActiveContextForApi();
    const ctxKey = ctx
      ? `${ctx.type}:${ctx.organizationId ?? ""}:${ctx.clientCompanyId ?? ""}`
      : "anon";
    return analyticsCache.getTrends(async () => {
      return await apiService.get<TrendAnalysisResponse>(
        API_ENDPOINTS.ANALYTICS.TREND_ANALYSIS,
      );
    }, `analytics:trends:${ctxKey}`);
  }

  /**
   * Dashboard em tempo real
   */
  async getRealTimeDashboard(): Promise<RealTimeDashboardResponse> {
    return await apiService.get<RealTimeDashboardResponse>(
      API_ENDPOINTS.ANALYTICS.DASHBOARD,
    );
  }

  /**
   * Métricas de performance do ML
   */
  async getMLPerformance(): Promise<MLPerformanceResponse> {
    return await apiService.get<MLPerformanceResponse>(
      API_ENDPOINTS.ANALYTICS.ML_PERFORMANCE,
    );
  }

  /**
   * Status do sistema de analytics
   */
  async getAnalyticsStatus(): Promise<AnalyticsStatusResponse> {
    return await apiService.get<AnalyticsStatusResponse>(
      API_ENDPOINTS.ANALYTICS.STATUS,
    );
  }

  // ===== MÉTODOS DE CACHE E OTIMIZAÇÃO =====

  /**
   * Limpar cache de analytics
   */
  async clearAnalyticsCache(): Promise<{ success: boolean; message: string }> {
    return await apiService.post(API_ENDPOINTS.CACHE.CLEAR);
  }

  /**
   * Obter estatísticas de cache
   */
  async getCacheStats(): Promise<CacheStatsResponse> {
    return await apiService.get<CacheStatsResponse>(API_ENDPOINTS.CACHE.STATS);
  }

  // ===== MÉTODOS DE MONITORAMENTO =====

  /**
   * Obter métricas de monitoramento
   */
  async getMonitoringMetrics(): Promise<MonitoringMetricsResponse> {
    return await apiService.get<MonitoringMetricsResponse>(
      API_ENDPOINTS.MONITORING.METRICS,
    );
  }

  /**
   * Obter alertas do sistema
   */
  async getAlerts(): Promise<AlertsResponse> {
    return await apiService.get<AlertsResponse>(
      API_ENDPOINTS.MONITORING.ALERTS,
    );
  }

  // ===== MÉTODOS UTILITÁRIOS =====

  /**
   * Verificar se o sistema de analytics está disponível
   */
  async checkAnalyticsAvailability(): Promise<boolean> {
    try {
      await this.getAnalyticsStatus();
      return true;
    } catch (error) {
      console.warn("Sistema de analytics não disponível:", error);
      return false;
    }
  }

  /**
   * Obter dados mockados para desenvolvimento
   */
  getMockAnalyticsData(): AnalyticsData {
    return {
      total_posts: 150,
      avg_engagement: 4.2,
      platform_performance: {
        stats: {
          linkedin: { mean: 5.2, std: 1.8, count: 45 },
          facebook: { mean: 3.8, std: 1.5, count: 38 },
          instagram: { mean: 4.1, std: 2.1, count: 67 },
        },
        best_platform: "linkedin",
        insights: [
          "LinkedIn tem 37% mais engajamento que outras plataformas",
          "Posts com imagens performam 25% melhor",
          "Horário ideal: 9h às 11h",
        ],
      },
      time_performance: {
        hourly_performance: {
          "9": { mean: 5.8, std: 1.2 },
          "10": { mean: 5.5, std: 1.4 },
          "11": { mean: 4.9, std: 1.6 },
          "14": { mean: 4.2, std: 1.8 },
          "15": { mean: 4.1, std: 1.7 },
          "16": { mean: 3.8, std: 1.9 },
        },
        best_hour: 9,
        insights: [
          "Manhã (9h-11h) tem melhor performance",
          "Evite publicar após 18h",
          "Finais de semana têm 30% menos engajamento",
        ],
      },
      content_insights: {
        correlations: {
          content_length: 0.15,
          hashtag_count: 0.08,
          has_image: 0.25,
          call_to_action: 0.12,
        },
        insights: [
          "Posts com imagens têm 25% mais engajamento",
          "Call-to-action aumenta engajamento em 12%",
          "Conteúdo entre 100-200 caracteres performa melhor",
        ],
      },
      recommendations: [
        {
          type: "timing",
          priority: "high",
          title: "Otimizar horários de publicação",
          description: "Focar em horários de maior engajamento (9h-11h)",
          impact: "high",
        },
        {
          type: "content",
          priority: "medium",
          title: "Aumentar uso de imagens",
          description: "Incluir imagens em 80% dos posts",
          impact: "medium",
        },
        {
          type: "platform",
          priority: "low",
          title: "Expandir presença no LinkedIn",
          description: "Plataforma com melhor performance",
          impact: "high",
        },
      ],
    };
  }
}

// Exportar instância singleton
const analyticsService = new AnalyticsService();
export default analyticsService;
